import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePlaidLink } from 'react-plaid-link';
import { Building2, CheckCircle, Loader2, AlertCircle, ShieldCheck } from 'lucide-react';
import { publicApi } from '../services/api';

type PageState = 'loading' | 'invalid' | 'ready' | 'connecting' | 'success' | 'error';

interface InviteData {
  employeeName: string;
  restaurantName: string;
  hasExistingBank: boolean;
}

interface ConnectedAccount {
  bankName: string;
  accountLast4: string;
}

export default function ConnectBankPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [pageState, setPageState] = useState<PageState>('loading');
  const [inviteData, setInviteData] = useState<InviteData | null>(null);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [connectedAccount, setConnectedAccount] = useState<ConnectedAccount | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Validate the invitation token on mount
  useEffect(() => {
    if (!token) {
      setPageState('invalid');
      setErrorMessage('No invitation token provided');
      return;
    }

    const validateToken = async () => {
      try {
        const response = await publicApi.validateBankInvite(token);
        if (response.success) {
          setInviteData(response.data);
          setPageState('ready');
        } else {
          setPageState('invalid');
          setErrorMessage(response.error?.message || 'Invalid invitation link');
        }
      } catch (err: any) {
        setPageState('invalid');
        setErrorMessage(err.message || 'Invalid or expired invitation link');
      }
    };

    validateToken();
  }, [token]);

  // Fetch Plaid link token when ready to connect
  const fetchLinkToken = async () => {
    if (!token) return;

    try {
      setPageState('connecting');
      const response = await publicApi.getBankLinkToken(token);
      if (response.success) {
        setLinkToken(response.data.linkToken);
      } else {
        setPageState('error');
        setErrorMessage(response.error?.message || 'Failed to initialize bank connection');
      }
    } catch (err: any) {
      setPageState('error');
      setErrorMessage(err.message || 'Failed to initialize bank connection');
    }
  };

  // Handle Plaid success
  const handlePlaidSuccess = useCallback(
    async (publicToken: string, metadata: any) => {
      if (!token) return;

      try {
        const accountId = metadata.accounts[0]?.id;
        if (!accountId) {
          setPageState('error');
          setErrorMessage('No account selected');
          return;
        }

        const response = await publicApi.exchangeBankToken(token, {
          publicToken,
          accountId,
        });

        if (response.success) {
          setConnectedAccount({
            bankName: response.data.bankName,
            accountLast4: response.data.accountLast4,
          });
          setPageState('success');
        } else {
          setPageState('error');
          setErrorMessage(response.error?.message || 'Failed to connect bank account');
        }
      } catch (err: any) {
        setPageState('error');
        setErrorMessage(err.message || 'Failed to connect bank account');
      }
    },
    [token]
  );

  // Plaid Link hook
  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: handlePlaidSuccess,
    onExit: (err) => {
      if (err) {
        setPageState('ready');
      } else {
        setPageState('ready');
      }
    },
  });

  // Open Plaid Link when token is ready
  useEffect(() => {
    if (linkToken && ready) {
      open();
    }
  }, [linkToken, ready, open]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-amber-600">Mise</h1>
          <p className="text-slate-500 mt-1">Direct Deposit Setup</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-xl shadow-xl p-8">
          {/* Loading State */}
          {pageState === 'loading' && (
            <div className="text-center py-8">
              <Loader2 className="w-12 h-12 animate-spin text-amber-600 mx-auto" />
              <p className="mt-4 text-slate-600">Validating your invitation...</p>
            </div>
          )}

          {/* Invalid Token State */}
          {pageState === 'invalid' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="mt-4 text-xl font-semibold text-slate-900">
                Invalid or Expired Link
              </h2>
              <p className="mt-2 text-slate-600">
                {errorMessage || 'This invitation link is no longer valid.'}
              </p>
              <p className="mt-4 text-sm text-slate-500">
                Please contact your manager to request a new invitation.
              </p>
            </div>
          )}

          {/* Ready State */}
          {pageState === 'ready' && inviteData && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
                  <Building2 className="w-8 h-8 text-amber-600" />
                </div>
                <h2 className="mt-4 text-xl font-semibold text-slate-900">
                  Connect Your Bank Account
                </h2>
                <p className="mt-2 text-slate-600">
                  Hi <span className="font-medium">{inviteData.employeeName}</span>,<br />
                  set up direct deposit for{' '}
                  <span className="font-medium">{inviteData.restaurantName}</span>
                </p>
              </div>

              {inviteData.hasExistingBank && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-800">
                    <strong>Note:</strong> You already have a bank account connected.
                    Connecting a new account will replace your existing one.
                  </p>
                </div>
              )}

              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-medium text-slate-900 mb-2">How it works</h3>
                <ol className="text-sm text-slate-600 space-y-2">
                  <li>1. Click the button below to securely connect</li>
                  <li>2. Select your bank and log in</li>
                  <li>3. Choose the account for deposits</li>
                  <li>4. Done! Payouts will be deposited directly</li>
                </ol>
              </div>

              <div className="flex items-center gap-3 text-sm text-slate-600 bg-green-50 p-4 rounded-lg">
                <ShieldCheck className="w-8 h-8 text-green-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-green-900">Powered by Plaid</p>
                  <p className="text-green-700">
                    Bank-level security. Your credentials are never shared with{' '}
                    {inviteData.restaurantName}.
                  </p>
                </div>
              </div>

              <button
                onClick={fetchLinkToken}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3 px-6 rounded-lg flex items-center justify-center gap-2 transition-colors"
              >
                <Building2 className="w-5 h-5" />
                Connect Bank Account
              </button>
            </div>
          )}

          {/* Connecting State */}
          {pageState === 'connecting' && (
            <div className="text-center py-8">
              <Loader2 className="w-12 h-12 animate-spin text-amber-600 mx-auto" />
              <p className="mt-4 text-slate-600">Initializing secure connection...</p>
            </div>
          )}

          {/* Error State */}
          {pageState === 'error' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="mt-4 text-xl font-semibold text-slate-900">
                Something Went Wrong
              </h2>
              <p className="mt-2 text-slate-600">{errorMessage}</p>
              <button
                onClick={() => setPageState('ready')}
                className="mt-6 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Success State */}
          {pageState === 'success' && connectedAccount && (
            <div className="text-center py-8 space-y-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900">
                  Bank Account Connected!
                </h2>
                <p className="mt-2 text-slate-600">
                  {connectedAccount.bankName} ****{connectedAccount.accountLast4}
                </p>
              </div>
              <p className="text-slate-600">
                Your payouts will now be deposited directly to this account.
              </p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-left">
                <p className="text-sm text-green-800">
                  <strong>You're all set!</strong> You can close this page now.
                  Your manager has been notified that your bank account is connected.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-slate-500 mt-6">
          &copy; {new Date().getFullYear()} Mise. Secure bank connections powered by Plaid.
        </p>
      </div>
    </div>
  );
}
